import { useTranslation } from 'react-i18next';
import type { ScientificObjectRef } from '../../../scientific-objects/ScientificObjectRef';
import type { ScientificObjectSummary } from '../../../scientific-objects/ScientificObjectSummary';
import type { ExternalProteinIdentifierNamespace, Protein } from './Protein';
import { getProtein } from './proteinApi';
import { RelationshipList } from '../../../knowledge/relationships/RelationshipList';
import { useAsyncResource } from '../../../hooks/useAsyncResource';

interface ProteinWorkspaceProps {
  readonly reference: ScientificObjectRef;
  readonly summary: ScientificObjectSummary;
}

const IDENTIFIER_LABELS: Record<ExternalProteinIdentifierNamespace, string> = {
  UNIPROT: 'UniProt',
  NCBI_PROTEIN: 'NCBI Protein',
  ENSEMBL_PROTEIN: 'Ensembl Protein'
};

export function ProteinWorkspace({ reference, summary }: ProteinWorkspaceProps) {
  const { t } = useTranslation();
  const state = useAsyncResource(() => getProtein(reference.id), [reference.id], 'Unable to load this protein.');

  return (
    <main className="protein-workspace" data-object-id={reference.id}>
      <header className="protein-heading">
        <p className="eyebrow">{t('proteinWorkspace.eyebrow')}</p>
        <h1>{summary.displayName}</h1>
        <p className="protein-id">{reference.id}</p>
      </header>
      {state.status === 'loading' ? <p>{t('proteinWorkspace.loading')}</p> : null}
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' ? <ProteinDetails protein={state.value} /> : null}
      <RelationshipList reference={reference} direction="incoming" relationshipType="ENCODES" />
      <RelationshipList reference={reference} direction="incoming" relationshipType="TRANSLATES_TO" />
    </main>
  );
}

function ProteinDetails({ protein }: { readonly protein: Protein }) {
  const { t } = useTranslation();
  return (
    <div className="protein-details">
      <section aria-labelledby="protein-overview-heading">
        <h2 id="protein-overview-heading">{t('proteinWorkspace.overview')}</h2>
        <dl className="protein-facts">
          <div>
            <dt>{t('proteinWorkspace.preferredName')}</dt>
            <dd>{protein.preferredName}</dd>
          </div>
          <div>
            <dt>{t('proteinWorkspace.length')}</dt>
            <dd>
              {protein.length} {t('proteinWorkspace.aminoAcids')}
            </dd>
          </div>
        </dl>
      </section>
      <section aria-labelledby="protein-identifiers-heading">
        <h2 id="protein-identifiers-heading">{t('proteinWorkspace.identifiers')}</h2>
        {protein.externalIdentifiers.length === 0 ? (
          <p>{t('proteinWorkspace.noIdentifiers')}</p>
        ) : (
          <dl className="protein-identifiers">
            {protein.externalIdentifiers.map((identifier) => (
              <div key={`${identifier.namespace}:${identifier.value}`}>
                <dt>{IDENTIFIER_LABELS[identifier.namespace]}</dt>
                <dd>{identifier.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
      <section aria-labelledby="protein-sequence-heading">
        <h2 id="protein-sequence-heading">{t('proteinWorkspace.sequence')}</h2>
        <pre className="protein-sequence">{protein.aminoAcidSequence}</pre>
      </section>
    </div>
  );
}
